import { useCreateItemModal } from '@/hooks/useCreateItemModal'
import type { Item, ItemList, ProjectList } from '@/types'
import { cn } from '@appfluence/classnames'
import { Button } from '@fluentui/react-components'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useResizeDetector } from 'react-resize-detector'
import { useReactToPrint } from 'react-to-print'
import { markItemAsModified, sendItem } from '../../common/src/actions/combinedAPI'
import { EVENT_EXTRA } from '../../common/src/eventTracking/amplitudeEvents'
import { itemHelper } from '../../common/src/helpers'
import { dateToTimestampInSeconds } from '../../common/src/helpers/dateHelper'
import { useMergeState } from '../../common/src/hooks/enhancedHooks'
import { Loading } from '../../components/basic/Loading'
import { SOURCES, linkSubject } from '../../reactions/linkSubject'
import { FullCalendarProps, View } from './PMCalendar.Common'
import { Header } from './PMCalendar.Header'
import { CalendarProjectsModal } from './PMCalendar.ProjectsModal'
import './PMCalendar.scss'
import { adaptAllDayEventDatesToPM, renderEvent, useKeyboardShortcuts, usePMData } from './PMCalendarUtils'
import { GlobalCalendarFiltersPanel } from './PMCalendar.Filters'
import { Filter } from '@/components/BundledIcons'

interface IPMCalendarProps {
  global: boolean
  items: ItemList
  projects: ProjectList
  readonly: boolean
  loading?: boolean
  onChangeRenderInterval?: (interval: [Date, Date]) => void
}

interface SelectData {
  allDay: boolean
  startDate: Date
  dueDate: Date
}

interface IState {
  view: View
  title: string
  activeStart?: Date
  activeEnd?: Date
  currentStart?: Date
  currentEnd?: Date
  printing: boolean
  projectsModalOpened: boolean
  filtersModalOpened: boolean
}

const defaultState: IState = {
  title: '',
  view: View.Month,
  printing: false,
  projectsModalOpened: false,
  filtersModalOpened: false,
} as const

const resizeDetectorOptions = {
  handleWidth: true,
  handleHeight: true,
  refreshMode: 'throttle',
  refreshRate: 1000 / 30,
  refreshOptions: {
    trailing: true,
  },
} as const satisfies Parameters<typeof useResizeDetector>[0]

interface IForwardRef {
  print: () => void
}

export const PMCalendar = forwardRef<IForwardRef, IPMCalendarProps>(
  ({ items, projects, readonly, global, onChangeRenderInterval, loading }, parentRef) => {
    const dispatch = useDispatch()
    const FCRef = useRef<FullCalendar>(null)
    const [state, setState] = useMergeState<IState>(defaultState)

    const setProjectModalOpen = (open: boolean) => setState({ projectsModalOpened: open })
    const openProjectsModal = () => setState({ projectsModalOpened: true })
    const openFiltersModal = () => setState({ filtersModalOpened: true })
    const createItemModal = useCreateItemModal()
    const { t } = useTranslation()
    const containerRef = useRef(null)
    const updateViewData = useCallback(() => {
      const view = FCRef.current?.getApi().view
      setState({
        title: FCRef.current?.getApi().view.title ?? '',
        view: (view?.type as View) ?? View.Month,
        activeStart: view!.activeStart,
        activeEnd: view!.activeEnd,
        currentStart: view!.currentStart,
        currentEnd: view!.currentEnd,
      })
    }, [setState])

    useKeyboardShortcuts(FCRef)

    useEffect(() => {
      if (state.currentEnd && state.currentStart) {
        onChangeRenderInterval?.([state.currentStart, state.currentEnd])
      }
    }, [state.currentStart, state.currentEnd, onChangeRenderInterval])

    const beforeGetContentPromiseSolver = useRef<(a: any) => void>(() => undefined)
    const handlePrint = useReactToPrint({
      content: () => containerRef.current,
      onBeforeGetContent: () => {
        return new Promise(resolve => {
          beforeGetContentPromiseSolver.current = resolve
          setState({ printing: true })
        })
      },
      onAfterPrint: () => {
        setState({ printing: false })
      },
    })

    useEffect(() => {
      if (state.printing) {
        FCRef.current?.getApi().updateSize()
        setTimeout(() => {
          beforeGetContentPromiseSolver.current?.(undefined)
        }, 500)
      }
    }, [state.printing])

    useImperativeHandle(
      parentRef,
      () => ({
        print: () => {
          handlePrint()
        },
      }),
      [handlePrint]
    )

    useEffect(() => void updateViewData(), [updateViewData])
    const { width, height, ref: resizeRef } = useResizeDetector(resizeDetectorOptions)
    const { events, loading: singleProjectLoading, projectInfoMap } = usePMData({ items, projects })
    const eventClick = useCallback<Required<FullCalendarProps>['eventClick']>(({ event }) => {
      linkSubject.next({
        urlData: itemHelper.getUriData(event.extendedProps.pmItem),
        source: SOURCES.CALENDAR,
      })
    }, [])
    useLayoutEffect(() => {
      FCRef.current?.getApi().updateSize()
    }, [width])
    const eventChange = useCallback<Required<FullCalendarProps>['eventChange']>(
      ({ event }) => {
        let item = event.extendedProps.pmItem as Item
        item = item.withMutations(i => {
          if (event.allDay) {
            const [start, end] = adaptAllDayEventDatesToPM([event.start!, event.end!])
            i.set('startDate', dateToTimestampInSeconds(start))
            i.set('dueDate', dateToTimestampInSeconds(end))
          } else {
            i.set('startDate', dateToTimestampInSeconds(event.start!))
            i.set('dueDate', dateToTimestampInSeconds(event.end!))
          }
        })
        dispatch(markItemAsModified(item))
        dispatch(sendItem(item))
      },
      [dispatch]
    )

    const eventSelect = useCallback<Required<FullCalendarProps>['select']>(
      event => {
        let context: SelectData
        if (event.view.type === View.Month) {
          const [startDate, dueDate] = adaptAllDayEventDatesToPM([event.start!, event.end!])
          context = {
            allDay: true,
            startDate,
            dueDate,
          }
        } else if (event.allDay) {
          const [startDate, dueDate] = adaptAllDayEventDatesToPM([event.start!, event.end!])
          context = {
            allDay: true,
            startDate,
            dueDate,
          }
        } else {
          context = {
            allDay: false,
            startDate: event.start,
            dueDate: event.end,
          }
        }

        createItemModal({
          mode: EVENT_EXTRA.CREATE_ITEM.MODE.HOME_AGENDA,
          displayProjectSelector: global,
          initialProject: projects.first(),
          open: false,
          completion: (i: unknown) => {
            linkSubject.next({
              urlData: itemHelper.getUriData(i),
              source: SOURCES.CALENDAR,
            })
          },
          ...context,
        })
      },
      [createItemModal, projects, global]
    )
    const loadingCN = cn({
      pm_hidden: !singleProjectLoading,
    })
    const printingCN = cn({
      pm_hidden: !state.printing,
    })
    //FCRef.current?.getApi().view.title
    const calendarCN = cn('relative flex-1 w-full overflow-hidden', {
      pm_hidden: singleProjectLoading,
    })
    const containerCN = cn('pm-flex-container flex-col', {
      'calendar-print-page': state.printing,
    })
    return (
      <>
        {global && (
          <GlobalCalendarFiltersPanel
            isOpen={state.filtersModalOpened}
            onDismiss={() => setState({ filtersModalOpened: false })}
          />
        )}
        <CalendarProjectsModal
          projectInfoMap={projectInfoMap}
          projects={projects}
          isOpen={state.projectsModalOpened}
          setIsOpen={setProjectModalOpen}
        />
        <Loading className={loadingCN} />
        <Loading className={printingCN} label={t('calendar.preparing_print')} />
        <div className={containerCN} ref={containerRef}>
          {state.printing && (
            <style type="text/css" media="print">
              {
                '\
  @page { size: landscape; }\
'
              }
            </style>
          )}
          <Header
            buttonsLeft={
              <>
                <Button onClick={openProjectsModal}>{t('calendar.projects')}</Button>
                <Button onClick={openFiltersModal} icon={<Filter />} />
              </>
            }
            title={state.title}
            FCRef={FCRef}
            updateViewData={updateViewData}
            view={state.view}
            loading={loading}
          />
          <div className={calendarCN} ref={resizeRef}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              ref={FCRef}
              viewClassNames="Calendar"
              height={height}
              events={events}
              editable
              eventContent={renderEvent}
              eventClick={eventClick}
              eventChange={eventChange}
              select={eventSelect}
              selectable
              dayMaxEvents
              expandRows
              headerToolbar={false}
            />
          </div>
        </div>
      </>
    )
  }
)
